import {useNavigate} from "react-router-dom";
import {QueryFunctionContext, useInfiniteQuery, useMutation} from "@tanstack/react-query";
import {deleteProducerNew, getProducersNew} from "../../../../data/api/Customer";
import {ProducerModelNew} from "../../../../data/model/Producer";
import {
    Box,
    Button,
    Flex,
    Heading,
    Image,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Stack,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr
} from "@chakra-ui/react";
import * as React from "react";
import {useState} from "react";
import {AddIcon, DeleteIcon, EditIcon, ViewIcon} from "@chakra-ui/icons";

interface ProducersResponse {
    isLastPage: boolean;
    totPage: number;
    currentPage: number;
    data: ProducerModelNew[];
}

export default function ProducersTablePage(): JSX.Element {
    const navigate = useNavigate();

    const {
        data,
        isLoading,
        isError,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage
    } = useInfiniteQuery<ProducersResponse, Error>({
        queryKey: ["producers"],
        initialPageParam: 0,
        queryFn: async ({ pageParam }: QueryFunctionContext) => {
            const res = await getProducersNew({ page: pageParam as number });
            return res?.data.data as ProducersResponse;
        },
        getNextPageParam: (lastPage) =>
            lastPage.isLastPage ? undefined : lastPage.currentPage + 1
    });

    const producers: ProducerModelNew[] =
        data?.pages.flatMap((page) => page.data) ?? [];

    return (
        <Box p={6}>
            <Flex justify="space-between" align="center" mb={4}>
                <Heading size="lg">Tutti i Produttori</Heading>
                <Button
                    leftIcon={<AddIcon />}
                    colorScheme="teal"
                    onClick={() => navigate("/producer/add")}
                >
                    Aggiungi
                </Button>
            </Flex>

            {isLoading && (
                <Flex justify="center" py={10}>
                    <Spinner size="xl" />
                </Flex>
            )}

            {isError && (
                <Text color="red.500" textAlign="center">
                    Errore nel caricamento
                </Text>
            )}

            {!isLoading && (
                <Box overflowX="auto">
                    <Table variant="simple">
                        <Thead>
                            <Tr>
                                <Th>Immagine</Th>
                                <Th>ID</Th>
                                <Th>Nome</Th>
                                <Th>Azioni</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {producers?.map((prod) => (
                                <ProducerRow
                                    key={prod?.id || ""}
                                    producer={prod}
                                    onEdit={() => navigate(`/producer/edit/${prod?.id || ""}`)}
                                />
                            ))}
                        </Tbody>
                    </Table>
                </Box>
            )}

            <Flex justify="center" mt={4}>
                {hasNextPage ? (
                    <Button
                        onClick={() => fetchNextPage()}
                        isLoading={isFetchingNextPage}
                    >
                        Carica altro
                    </Button>
                ) : (
                    <Text color="gray.500">Nessun altro produttore</Text>
                )}
            </Flex>
        </Box>
    );
}

type ProducerRowProps = {
    producer: ProducerModelNew;
    onEdit: () => void;
};

function ProducerRow({ producer, onEdit }: ProducerRowProps): JSX.Element {
    const [isOpen, setOpen] = useState(false);

    const deleteMutation = useMutation({
        mutationFn: (id: string) => deleteProducerNew(id),
        onSuccess: () => window.location.reload()
    });

    return (
        <>
            <Tr>
                <Td>
                    <Image
                        boxSize="50px"
                        objectFit="cover"
                        src={producer?.images?.find((i) => i.isMain)?.url}
                        alt={producer?.name}
                    />
                </Td>
                <Td>{producer?.id}</Td>
                <Td>{producer?.name}</Td>
                <Td>
                    <Stack direction="row" spacing={2}>
                        <Button
                            size="sm"
                            leftIcon={<ViewIcon />}
                            onClick={() => setOpen(true)}
                        >
                            Apri
                        </Button>
                        <Button size="sm" leftIcon={<EditIcon />} onClick={onEdit}>
                            Modifica
                        </Button>
                        <Button
                            size="sm"
                            leftIcon={<DeleteIcon />}
                            // colorScheme="red"
                            color={'red'}
                            onClick={() => deleteMutation.mutate(producer?.id || '')}
                        >
                            Elimina
                        </Button>
                    </Stack>
                </Td>
            </Tr>

            <ProducerDetailModal
                isOpen={isOpen}
                onClose={() => setOpen(false)}
                producer={producer}
            />
        </>
    );
}

type ProducerDetailModalProps = {
    isOpen: boolean;
    onClose: () => void;
    producer: ProducerModelNew;
};

export function ProducerDetailModal({ isOpen, onClose, producer }: {isOpen: boolean; onClose: () => void; producer: ProducerModelNew}) {
    const fields = [
        ["Nome", producer.name],
        ["Email", producer.email],
        ["Descrizione", producer.description],
        ["Sito web", producer.website],
        ["Telefono", producer.phone],
        ["Indirizzo", producer.address],
        ["Partita IVA", producer.piva],
        ["Referente", `${producer.referentName} ${producer.referentSurname}`],
        ["Email Referente", producer.referentEmail],
        ["Telefono Referente", producer.referentPhone],
    ];

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Dettagli Produttore</ModalHeader>
                <ModalCloseButton />
                <ModalBody maxH={"70vh"} overflowY={'auto'}>
                    <Stack spacing={4}>
                        {fields
                            .filter(([, value]) => Boolean(value))
                            .map(([label, value]) => (
                                <Stack key={label} spacing={1}>
                                    <Text fontSize="sm" color="gray.600">
                                        {label}
                                    </Text>
                                    <Text>{value}</Text>
                                </Stack>
                            ))}
                    </Stack>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}

// function ProducerDetailModal({
//                                  isOpen,
//                                  onClose,
//                                  producer
//                              }: ProducerDetailModalProps): JSX.Element {
//     return (
//         <CustomModal
//             isOpen={isOpen}
//             onClose={onClose}
//             content={
//                 <Box>
//                     <Heading size="md" mb={4}>
//                         Dettagli Produttore
//                     </Heading>
//                     <Image
//                         boxSize="200px"
//                         objectFit="cover"
//                         src={producer?.images?.find((i) => i.isMain)?.url}
//                         alt={producer?.name}
//                         mb={4}
//                     />
//                     <Text><strong>ID:</strong> {producer?.id}</Text>
//                     <Text><strong>Nome:</strong> {producer?.name}</Text>
//                     {producer?.description && <Text><strong>Descrizione:</strong> {producer?.description}</Text>}
//                     {producer?.email && <Text><strong>Email:</strong> {producer?.email}</Text>}
//                     {producer?.website && <Text><strong>Sito Web:</strong> {producer?.website}</Text>}
//                     {/* Aggiungi altre info se necessario */}
//                 </Box>
//             }
//         />
//     );
// }

// export default function ProducersPage() {
//     const [page, setPage] = useState(1);
//
//     const responseAll = useQuery({
//         queryKey: ['get-producers-all', page],
//         queryFn: () => getProducersNew({page: page - 1}),
//         refetchOnWindowFocus: "always",
//     });
//
//     const {
//         data: producers,
//         totPage,
//         isLoading,
//         refetch
//     } = usePaginationData<ProducerModelNew[]>(responseAll);
//
//     const navigate = useNavigate();
//     const userType = localStorage.getItem("userType");
//
//     return (
//         <VStack w={"100%"} alignItems={"flex-start"} overflow={"auto"}
//                 css={scrollbarCss({marginLeft: 0, marginRight: 0})}>
//
//             {userType === "admin" &&
//                 <HStack cursor={"pointer"} h={"64px"} px={"24px"} borderRadius={"24px"} position={"absolute"}
//                         alignItems={"center"}
//                         justifyContent={"center"} bottom={"16px"} right={"16px"} backgroundColor={"secondary.200"}
//                         _hover={{backgroundColor: "tertiary.200"}} onClick={() => {
//                     navigate("/producer/add");
//                 }}>
//                     <AddIcon w={"16px"} h={"16px"} alignSelf={"center"}/>
//                     <Text ms={"8px"} fontWeight={"bold"}>Aggiungi</Text>
//                 </HStack>}
//
//             <HStack pt={"24px"}>
//                 <Text fontSize={"24px"} fontWeight={"bold"} mt={"24px"} ms={"24px"}>Tutti i produttori</Text>
//
//                 <PaginationInputComponent setPage={setPage} page={page} totPage={totPage}/>
//             </HStack>
//
//             <Box mt={"18px"} mx={"24px"} alignSelf={"stretch"} backgroundColor={"surfaceContainer.light"}
//                  borderRadius={"16px"} overflowX={"hidden"} mb={"72px"}
//                  css={scrollbarCss({marginLeft: 0, marginRight: 0})}>
//                 <TableComponent body={isLoading ? <CategoriesTableBodyLoading isAllCategories={true}/> :
//                     <ProducerTableBody data={producers} doRefetch={() => refetch()}
//                                        baseKey={'producer_row_index'}/>}
//                                 headers={["", "ID", "Nome", ""]}/>
//             </Box>
//         </VStack>
//     );
// }
//
// const ProducerTableBody = ({data, baseKey, doRefetch}: {
//     data?: ProducerModelNew[],
//     baseKey?: string,
//     doRefetch: () => void
// }) => {
//     const navigate = useNavigate();
//
//     const {mutateAsync} = useMutation({
//         mutationKey: ['delete-producer'],
//         mutationFn: (id: string) => deleteCategory(id),
//     });
//
//     return <Tbody>
//         {(data?.length ?? 0) > 0 && data?.map((value, index) => {
//             return <ProducerContentTable key={baseKey + "_row_" + index} value={value} index={index} baseKey={baseKey || ""}
//                                          doRefetch={doRefetch}/>
//         })}
//     </Tbody>
// }
//
// const ProducerContentTable = ({value, index, baseKey, doRefetch}: {
//     value: ProducerModelNew,
//     index: number,
//     baseKey: string,
//     doRefetch: () => void,
// }) => {
//     const {id, name, images} = value || {};
//     const {url: mainImage} = images?.find((value) => value.isMain) || {};
//     const navigate = useNavigate();
//
//     const {isOpen, onOpen, onClose} = useDisclosure();
//
//     const {mutateAsync} = useMutation({
//         mutationKey: ['delete-producer'],
//         mutationFn: (id: string) => deleteProducerNew(id),
//     });
//
//     return <>
//         <Tr key={`${baseKey}_${index}`}>
//             <Td>
//                 <Stack w={"64px"} h={"64px"} minW={"64px"} minH={"64px"} backgroundColor={"white"}
//                        borderWidth={"1px"} borderColor={"white"} borderRadius={"16px"} overflow={"hidden"}>
//                     <Image w={"64px"} h={"64px"} minW={"64px"} minH={"64px"} objectFit={"cover"} aspectRatio={1}
//                            src={mainImage}/>
//                 </Stack>
//             </Td>
//             <Td>{id || "-"}</Td>
//             <Td>{name || "-"}</Td>
//             <Td>
//                 <HStack spacing={"16px"}>
//                     <TableIcon icon={<EyeIcon/>} onClick={onOpen}/>
//                     <TableIcon icon={<EditIcon/>} onClick={() => {
//                     navigate(`/producer/edit/${id}`);
//                 }}/>
//                     <TableIcon icon={<DeleteIcon/>} onClick={() => {
//                         if (id) {
//                             mutateAsync(id).then(doRefetch);
//                         }
//                     }}/>
//                 </HStack>
//             </Td>
//         </Tr>
//         <CustomModal isOpen={isOpen} onClose={onClose} content={<ProducerDetailSection model={value}/>}/>
//     </>
// }
//
// const ProducerDetailSection = ({model}: { model?: ProducerModelNew }) => {
//     const {
//         id,
//         images,
//         name,
//         email,
//         description,
//         website,
//         address,
//         phone,
//         piva,
//         referentName,
//         referentSurname,
//         referentEmail,
//         referentPhone,
//     } = model || {};
//     const mainImage = images?.find((value) => value.isMain) || {};
//     const {url} = mainImage || "";
//
//     return <HStack py={"24px"}>
//         <Stack w={"256px"} h={"256px"} minW={"256px"} minH={"256px"} backgroundColor={"transparent"}
//                borderWidth={"1px"} borderColor={"white"} borderRadius={"16px"} overflow={"hidden"}>
//             <Image w={"256px"} h={"256px"} minW={"256px"} minH={"256px"} objectFit={'cover'} src={url}/>
//         </Stack>
//         <TableContainer py={"16px"}>
//             <Table variant={'none'}>
//                 <Tbody>
//                     {id && <AddTableRow name={"ID"}><Text>{id}</Text></AddTableRow>}
//                     {name && <AddTableRow name={"Nome"}><Text>{name}</Text></AddTableRow>}
//                     {description && <AddTableRow name={"Descrizione"}><Text
//                         wordBreak={"break-word"}>{description}</Text></AddTableRow>}
//                     {email && <AddTableRow name={"Email"}><Text>{email}</Text></AddTableRow>}
//                     {website && <AddTableRow name={"Sito web"}><Text>{website}</Text></AddTableRow>}
//                     {address && <AddTableRow name={"Indirizzo"}><Text>{address}</Text></AddTableRow>}
//                     {phone && <AddTableRow name={"Telefono"}><Text>{phone}</Text></AddTableRow>}
//                     {piva && <AddTableRow name={"Partita IVA"}><Text>{piva}</Text></AddTableRow>}
//                     {referentName && <AddTableRow name={"Nome referente"}><Text>{referentName}</Text></AddTableRow>}
//                     {referentSurname &&
//                         <AddTableRow name={"Cognome referente"}><Text>{referentSurname}</Text></AddTableRow>}
//                     {referentEmail && <AddTableRow name={"Email referente"}><Text>{referentEmail}</Text></AddTableRow>}
//                     {referentPhone &&
//                         <AddTableRow name={"Telefono referente"}><Text>{referentPhone}</Text></AddTableRow>}
//                 </Tbody>
//             </Table>
//         </TableContainer>
//     </HStack>
// }
